import React from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import Dialog from '@dashboard/components/Dialog';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import { toast } from '@dashboard/context/ToastContext';
import { pagesKey } from '@dashboard/lib/queryKeys';
import { fetcher } from '@dashboard/services/request';

const capitalize = text => text.charAt(0).toUpperCase() + text.slice(1);

const PageDeleteDialog = ({ open, onClose, page, label }) => {
  const { sellerId, token } = useAuth();
  const queryClient = useQueryClient();
  const displayName = page.name?.trim() || `this ${label}`;

  const deletePage = useMutation({
    mutationFn: () =>
      fetcher(`/sellers/${sellerId}/pages/${page._id}/delete`, token, { method: 'POST' }),
    onSuccess: () => {
      toast({ title: `${capitalize(label)} deleted`, type: 'success' });
      queryClient.invalidateQueries({ queryKey: pagesKey });
      onClose();
    },
    onError: error => {
      toast({
        title:
          error?.message || `Unable to delete this ${label}. Please try again or contact support.`,
        type: 'error',
        duration: 6000,
      });
      onClose();
    },
  });

  return (
    <Dialog
      title={`Delete ${displayName}?`}
      open={open}
      onCancel={onClose}
      cancelButton={{ label: 'Cancel', appearance: 'secondary' }}
      confirmButton={{
        label: `Delete ${displayName}`,
        appearance: 'secondary',
        className: 'block min-w-0 shrink truncate',
        disabled: deletePage.isPending,
        onClick: () => deletePage.mutate(),
      }}
    >
      <p className="text-sm text-gray-600 dark:text-muted-foreground">
        This action cannot be undone. Links to this {label} and any one-click upsells using it will
        stop working. Coupons limited only to this {label} will apply to every checkout page and
        event.
      </p>
    </Dialog>
  );
};

export default PageDeleteDialog;
